import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Badge, Card, CardBody, Col, Row, Table } from "reactstrap"
import Filters from "components/Common/Filters"
import TableShimmer from "components/Shared/TableShimmer"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { getApiLogs } from "store/actions/apilogsAction"
import { useSelector } from "react-redux"
import Paginations from "components/Common/Paginations"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import { EXPORT_DATA } from "helpers/url_helper"
import { pdfDownload, post } from "helpers/api_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"
import { getPerPageOptions, todayDate } from "components/Common/constants"
import { setItemsPerPage } from "store/reducers/apiLogsReducer"
import { API_LOG_ENDPOINT_OPTIONS } from "constants/apiLogEndpointOptions"
import { filterOptionsByPermissions } from "utils/filterOptionsByPermissions"

const ApiLogs = () => {
  const { t } = useTranslation()

  document.title = t("API Logs ")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const flatpickrRef = useRef(null)
  const minDate = searchParams.get("minDate") || todayDate
  const maxDate = searchParams.get("maxDate") || todayDate
  const statusParam =
    searchParams.get("status") !== null
      ? parseInt(searchParams.get("status"), 10)
      : -1
  const responseStatusParam =
    searchParams.get("responseStatus") !== null
      ? parseInt(searchParams.get("responseStatus"), 10)
      : 0
  const urlParam = searchParams.get("url") || ""
  const {
    loading,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    apiLogsList,
  } = useSelector(state => state.APILogs)

  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "0"),
    status: statusParam,
    responseStatus: responseStatusParam,
    url: urlParam || "",
  })
  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })

  const { userRole, accountType, authUser, userIntegrations } = useSelector(
    state => state.Login
  )

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  const endpointOptions = filterOptionsByPermissions(
    API_LOG_ENDPOINT_OPTIONS,
    userRole,
    accountType,
    undefined,
    userIntegrations
  )

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      13,
      "api_logs_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
    })
  }, [userRole])

  useEffect(() => {
    const allowedUrls = new Set(endpointOptions.map(option => option.value))
    if (selectedFilters.url && !allowedUrls.has(selectedFilters.url)) {
      setSelectedFilters(prev => ({ ...prev, url: "" }))
    }
  }, [endpointOptions, selectedFilters.url])

  useEffect(() => {
    getApiLogsData()
  }, [])

  const getApiLogsData = () => {
    const { sortOrder, status, responseStatus, url } = selectedFilters
    // console.log(selectedFilters)
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
      status: Number(status),
      responseStatus: Number(responseStatus),
      url,
    }
    dispatch(getApiLogs(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 0 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: -1 },
        { label: t("Success"), value: 1 },
        { label: t("Failure"), value: 0 },
      ],
      placeholder: t("Result(Success/Failure)"),
    },
    {
      name: "responseStatus",
      type: "select",
      options: [
        { label: t("All"), value: 0 },
        { label: t("Status Code 200"), value: 200 },
        { label: t("Status Code 400"), value: 400 },
      ],
      placeholder: t("Response Code"),
    },
    {
      name: "url",
      type: "select",
      options: endpointOptions.map(option => ({
        label: t(option.labelKey),
        value: option.value,
      })),
      placeholder: t("Select End Points"),
    },
  ]

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleFetchApiLogs = (page = 1) => {
    const { sortOrder, status, responseStatus, url } = selectedFilters
    const searchParams = new URLSearchParams()
    if (searchTerm) searchParams.set("search", searchTerm)
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())
    searchParams.set("status", status.toString())
    searchParams.set("responseStatus", responseStatus.toString())
    searchParams.set("url", url.toString())
    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
      status: Number(status), // default (-1), 0: failure and 1: success
      responseStatus: Number(responseStatus), //default(0),200 or 400
      url, ///shopping-cart/api/v1/getAllItems
    }

    dispatch(getApiLogs(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/api-logs?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchApiLogs(1)
  }

  const onPageChange = page => {
    handleFetchApiLogs(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleReset = () => {
    dispatch(setItemsPerPage(50))
    setDateRange({ min: todayDate, max: todayDate })
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate([todayDate, todayDate])
    }
    const payload = {
      perPage: 50,
      pageNo: 1,
      orderBy: 0,
      keyword: "",
      dateRange: {},
      status: -1,
      responseStatus: 0,
      url: "",
      dateRange: { min: todayDate, max: todayDate },
    }
    dispatch(getApiLogs(payload))
    navigate(`/api-logs`)
    setSelectedFilters({
      sortOrder: 0,
      status: -1,
      responseStatus: 0,
      url: "",
    })
    setSearchTerm("")
    // setDateRange({})
    // if (flatpickrRef.current) {
    //   flatpickrRef.current.flatpickr.clear()
    // }
  }

  const handleExcelDownload = async () => {
    const { status } = selectedFilters

    const payload = {
      status: 1,
      keyword: searchTerm || "",
      type: "API-LOGS",
      exportType: "xlsx",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await post(EXPORT_DATA, payload)
    if (response) {
      // const xlsxData = response
      // DownloadXLSX(xlsxData, t("API Logs"))
      const { statusCode, status, msg } = response
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
      } else {
        dispatch(showAlert({ text: msg, type: "error" }))
      }
    }
  }

  const exportToCSV = async () => {
    const { status } = selectedFilters

    const payload = {
      status: 1,
      keyword: searchTerm || "",
      type: "API-LOGS",
      exportType: "csv",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await post(EXPORT_DATA, payload)
    if (response) {
      // const csvData = response
      // DownloadCSV(csvData, t("API Logs"))
      const { statusCode, status, msg } = response
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
      } else {
        dispatch(showAlert({ text: msg, type: "error" }))
      }
    }
  }

  const perPageOptions = getPerPageOptions(totalCount)

  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />

        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("API Logs List")}
                isXLSX={false}
                isAddBtn={false}
                isExportData={loading ? false : authUser?.allowExports}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  setItemsPerPage={null}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Phone Number")}
                  colSize={4}
                />

                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={7} />
                  ) : apiLogsList.length === 0 ? (
                    <p className="text-center my-2">{t("No API logs found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr className="full_th">
                          <th>{t("S. No.")}</th>
                          <th>{t("Customer Name")}</th>
                          <th>{t("Phone Number")}</th>
                          <th>{t("Method")}</th>
                          <th>{t("Path")}</th>
                          <th>{t("Status Code")}</th>
                          <th>{t("Result")}</th>
                          <th>{t("Duration")}</th>
                          <th>{t("Happened")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiLogsList?.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() =>
                              navigate(
                                `/api-logs/api-logs-details?id=${item._id}`
                              )
                            }
                            className="cursor-pointer"
                          >
                            <td>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td>{item?.userInfo?.name || "-"}</td>
                            <td>
                              {item?.userInfo?.phone ? (
                                <LtrText>{item.userInfo.phone}</LtrText>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <p className="bg-primary w_fit_content px-2 text-white rounded fw-bold">
                                <small> {item?.data?.method || "-"}</small>
                              </p>
                            </td>
                            <td>{item?.data?.url || "-"}</td>
                            <td>
                              <span
                                className={`${
                                  item?.data?.responseStatus === 200
                                    ? "bg-success"
                                    : item?.data?.responseStatus === 500
                                    ? "bg-danger"
                                    : "bg-warning"
                                }
                                 text-white fw-bold px-1 rounded`}
                              >
                                <small>{item?.data?.responseStatus}</small>
                              </span>
                            </td>
                            <td>
                              {item?.data?.status === 1 &&
                              item?.data?.responseStatus ? (
                                <Badge className="bg-success">
                                  {t("Success")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Failed")}
                                </Badge>
                              )}
                            </td>
                            <td>{item?.data?.duration || "-"}</td>
                            <td>{formatDateTime(item?.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!loading && (
                  <div className="mt-3">
                    <Paginations
                      data={apiLogsList}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default ApiLogs
